import Button from '@app/shared/components/button/Button';
import Plus from '@app/shared/icons/Plus';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import * as projectActions from '@core/store/project-slice';
import { cloneCase, generateUniqueName } from '@app/shared/helpers';
import { ButtonAppearance } from '@app/shared/components/button/button-enums';
import { MAX_CASES_COUNT } from '@app/shared/constants/deal-thesis';
import { usePwcNavigate } from '@core/hooks/routing-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { v4 as uuidv4 } from 'uuid';
import {
  CapitalStructureDto,
  CaseDto,
  DividendInstrumentDateDto,
  DividendInstrumentDto,
  DividendSetDto,
} from '@app/shared/models/contracts/project-dto';
import { uiValuesSlice } from '@app/core/store/ui-values-slice';
import { selectCapitalStructure } from '@app/core/store/capital-structure-slice-selectors';
import { useCaseEventManagement } from './deal-thesis-case-table-2/hooks/useCaseEventManagement';

export const AddDealThesisCase = () => {
  const { l } = useLocale();
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.projectDraft);
  const capitalStructure: CapitalStructureDto = useAppSelector(selectCapitalStructure);
  const { navigate, generatePath } = usePwcNavigate();
  const { createEventSetForNewCase } = useCaseEventManagement(capitalStructure);

  const createUpdatedForecastDividendSets = (
    capitalStructure: CapitalStructureDto,
    sourceCase: CaseDto,
    newDividendSetId: string
  ): Record<string, DividendSetDto> => {
    const dividendEligibleInstruments = Object.entries(capitalStructure.instrumentDefinitions)
      .filter(([_, instrument]) => instrument.dividendEligible)
      .map(([instrumentId]) => instrumentId);

    const forecastYears = sourceCase.forecasts?.map((f) => f.forecastYear) || [];
    const dividendSetInstruments: Record<string, DividendInstrumentDto> = {};

    dividendEligibleInstruments.forEach((instrumentId) => {
      const dates: Record<string, DividendInstrumentDateDto> = {};
      forecastYears.forEach((year) => {
        dates[year] = { totalDividendValue: null };
      });
      dividendSetInstruments[instrumentId] = {
        instrumentId,
        dates,
      };
    });

    return {
      ...capitalStructure.forecastDividendSets,
      [newDividendSetId]: {
        dividendSetId: newDividendSetId,
        instruments: dividendSetInstruments,
      },
    };
  };

  const handleAddCase = () => {
    const caseNarrative = generateUniqueName(
      'Case',
      new Set(project.pwermInput.cases?.map((caseItem) => caseItem.narrative))
    );

    const clonedCase = cloneCase(project.pwermInput.cases[project.pwermInput.cases.length - 1]);
    const newCaseId = clonedCase.caseId;

    const capitalStructureId = clonedCase.capitalStructureId;
    const newDividendSetId = uuidv4();

    const updatedForecastDividendSets = createUpdatedForecastDividendSets(
      capitalStructure,
      clonedCase,
      newDividendSetId
    );

    const { eventSets: updatedEventSets, eventSetId } = createEventSetForNewCase(
      project.pwermInput.cases,
      clonedCase
    );

    const updatedCapitalStructures = {
      ...project.capitalStructures,
      [capitalStructureId]: {
        ...project.capitalStructures[capitalStructureId],
        forecastDividendSets: updatedForecastDividendSets,
        eventSets: updatedEventSets,
      },
    };

    dispatch(
      projectActions.updateProjectDraft({
        project: {
          ...project,
          capitalStructures: updatedCapitalStructures,

          pwermInput: {
            ...project.pwermInput,
            cases: [
              ...project.pwermInput.cases,
              {
                ...clonedCase,
                narrative: caseNarrative,
                probability: 0,
                caseOrder: project.pwermInput.cases.length,
                dividendSetId: newDividendSetId,
                eventSetId: eventSetId,
              },
            ],
          },
        },
      })
    );
    navigate(generatePath('../:caseId', { caseId: newCaseId }));
    dispatch(uiValuesSlice.actions.setIsCaseBeingAddedStatus(true));
  };

  return (
    <Button
      appearance={ButtonAppearance.DEFAULT_PRIMARY}
      startIcon={<Plus />}
      onClick={handleAddCase}
      disabled={project.pwermInput.cases?.length === MAX_CASES_COUNT}>
      {l('_AddCase')}
    </Button>
  );
};
